import React from 'react';
import { Image } from '@fiverr-private/media';
import { ResponsiveGridTemplatesType } from '@fiverr-private/theme/dist/cjs/src/components/Box/types';
import { Divider, Grid, GridItem } from '@fiverr-private/layout_components';
import { ResponsiveSpacingType } from '@fiverr-private/theme/src/components/Box/types';
import { translate } from '@fiverr-private/i18n-react';
import { MonthlyCredit, PackageData } from '../../types/complianceTypes';
import TextWithCheck from './TextWithCheck';
import TextWithLink from './TextWithLink';
import { BackgroundCheckPackageData, ComplianceType, ComplianceTypes, COPIES_PREFIX } from './types';

export const getComplianceImage = (complianceType: ComplianceType) => {
    const getImageUrl = (complianceType: ComplianceType) => {
        switch (complianceType) {
            case ComplianceTypes.BACKGROUND_CHECK:
                return 'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/76d511961d2567bcfee2e309208f0bcf-1733821524027/Request%20BG%20check%20modal-%20light.png';
            case ComplianceTypes.LEGAL_DOCS:
                return null;
            case ComplianceTypes.WORKFORCE_CLASSIFICATION:
                return 'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/c57d1c37c186d650b2238c1dfca484a9-1733821564560/Request%20classification%20report%20modal-%20light.png';
            default:
                return null;
        }
    };

    const imageUrl = getImageUrl(complianceType);
    if (!imageUrl) return null;

    return <Image alt={'image'} src={imageUrl} width="100%" height="auto" />;
};

export const getCSSParams = (complianceType: ComplianceType): { gap: ResponsiveSpacingType; size: string } =>
    complianceType === 'background_check' ? { gap: '8', size: 'lg' } : { gap: '7', size: 'md' };

export const getFooter = (complianceType: ComplianceType, link = 'special_terms') => {
    const footer = `${COPIES_PREFIX}.${complianceType}.footer`;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <TextWithLink body={footer} link={link} size={'b_xs'} color={'grey_1000'} />;
};

export const getCheckedComplianceGrid = (items: string[]) => {
    if (!items.length) return null;
    const mapGridRows: { [key: number]: ResponsiveGridTemplatesType } = {
        1: '100%',
        2: '50%_50%',
        3: '33%_33%_33%',
        4: '25%_25%_25%_25%',
    };
    const templateRows = mapGridRows[items.length];
    return (
        <>
            <Grid gap="3" templateRows={templateRows}>
                {items.map((item, index) => (
                    <GridItem key={index} display="inlineGrid">
                        <TextWithCheck text={item} />
                    </GridItem>
                ))}
            </Grid>
            <Divider direction="horizontal" />
        </>
    );
};

const getTimeEstimation = (timeEstimation: { min: number; max: number }, amount) =>
    `${timeEstimation?.min || 0}-${timeEstimation?.max || 0} ${amount}`;

export const getInfoGridPaymentData = (
    complianceType: ComplianceType,
    backgroundCheckPackageData: BackgroundCheckPackageData | undefined,
    workforceClassificationPackageData: PackageData | undefined
) => {
    const gridInfo: {
        packageTypeTitle: string;
        timeEstimation: string;
        amountInDollars: number;
        checkedComplianceGrid: JSX.Element | null;
        discountInPercents: number;
        monthlyCredit?: MonthlyCredit;
        currency: string;
    } = {
        packageTypeTitle: '',
        timeEstimation: '',
        amountInDollars: NaN,
        checkedComplianceGrid: null,
        discountInPercents: 0,
        currency: 'USD',
    };

    if (!backgroundCheckPackageData || !workforceClassificationPackageData) return gridInfo;

    switch (complianceType) {
        case ComplianceTypes.BACKGROUND_CHECK:
            gridInfo.packageTypeTitle = `${COPIES_PREFIX}.staticPackage.packageType.${backgroundCheckPackageData?.typeId}`;
            const backgroundCheckItemsIds = backgroundCheckPackageData?.packageItemsIds || [];
            const backgroundCheckIdsPrefix = `${COPIES_PREFIX}.staticPackage.packageItemsIds`;
            const backgroundCheckPackageItems = backgroundCheckItemsIds.map((itemId) =>
                translate(`${backgroundCheckIdsPrefix}.${itemId}`)
            ); // Translate each itemId
            gridInfo.checkedComplianceGrid = getCheckedComplianceGrid(backgroundCheckPackageItems);
            gridInfo.timeEstimation = getTimeEstimation(
                backgroundCheckPackageData?.timeEstimation,
                translate(`${COPIES_PREFIX}.staticPackage.timeEstimation.days`)
            );
            gridInfo.discountInPercents = backgroundCheckPackageData?.discountInPercents || 0;
            gridInfo.monthlyCredit = backgroundCheckPackageData?.monthlyCredit;
            gridInfo.amountInDollars = backgroundCheckPackageData?.price / 100;
            gridInfo.currency = backgroundCheckPackageData?.currency || 'USD';
            break;

        case ComplianceTypes.WORKFORCE_CLASSIFICATION:
            gridInfo.packageTypeTitle = `${COPIES_PREFIX}.classificationPackage.packageType.${workforceClassificationPackageData?.typeId}`;
            const workforceClassificationItemsIds = workforceClassificationPackageData?.packageItemsIds || [];
            const workforceClassificationIdsPrefix = `${COPIES_PREFIX}.classificationPackage.packageItemsIds`;
            const workforceClassificationPackageItems = workforceClassificationItemsIds.map((itemId) =>
                translate(`${workforceClassificationIdsPrefix}.${itemId}`)
            ); // Translate each itemId
            gridInfo.checkedComplianceGrid = getCheckedComplianceGrid(workforceClassificationPackageItems);
            gridInfo.timeEstimation = getTimeEstimation(
                workforceClassificationPackageData?.timeEstimation,
                translate(`${COPIES_PREFIX}.classificationPackage.timeEstimation.days`)
            );
            gridInfo.discountInPercents = workforceClassificationPackageData?.discountInPercents || 0;
            gridInfo.monthlyCredit = workforceClassificationPackageData?.monthlyCredit;
            gridInfo.amountInDollars = workforceClassificationPackageData?.price / 100;
            gridInfo.currency = workforceClassificationPackageData?.currency || 'USD';
            break;

        case ComplianceTypes.LEGAL_DOCS:
        default:
            break;
    }
    return gridInfo;
};
